import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getSupabaseClient, hasSupabaseConfig } from '../lib/supabaseClient';
import {
  pullCommitteesIntoRxDB,
  upsertOrganizationLocal,
  upsertProfileLocal,
  upsertMembershipLocal,
} from '../db/sync/membersSync';
import {
  readCachedOrgContext,
  useMeetingLiveStore,
  writeCachedOrgContext,
} from '../store/meetingLiveStore';
import {
  membershipHasModule,
  normalizeModules,
  normalizeRole,
  readActiveOrganizationId,
  writeActiveOrganizationId,
} from './moduleAccess';

const AuthContext = createContext(null);

function mapMembershipRow(row) {
  const orgRel = row.organizations;
  const org = Array.isArray(orgRel) ? orgRel[0] : orgRel;
  const role = normalizeRole(row.role);
  return {
    id: row.id,
    user_id: row.user_id,
    organization_id: row.organization_id,
    role,
    modules: normalizeModules(role, row.modules),
    is_active: row.is_active !== false,
    organization_name: org?.name ?? row.organization_name ?? '',
    quorum_threshold: org?.quorum_threshold ?? 50,
    bylaws_config: org?.bylaws_config ?? row.bylaws_config ?? {},
    organizations: org ?? null,
  };
}

/** Never block the UI forever on IndexedDB / RxDB. */
async function safeLocal(fn) {
  try {
    await fn();
  } catch (err) {
    console.warn('[Auth] local DB op failed', err?.message || err);
  }
}

export function AuthProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [activeOrganizationId, setActiveOrganizationId] = useState(null);
  const [allowOfflineAccess, setAllowOfflineAccess] = useState(false);
  /** UI gate for the platform console only — see the rpc call in refreshMemberships. */
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const membershipsRef = useRef([]);

  useEffect(() => {
    membershipsRef.current = memberships;
  }, [memberships]);

  const applyActiveOrg = useCallback(async (orgId, membershipList) => {
    const list = membershipList;
    if (!Array.isArray(list)) return null;
    const active = list.find((m) => m.organization_id === orgId && m.is_active);
    if (!active) {
      setActiveOrganizationId(null);
      writeActiveOrganizationId(null);
      useMeetingLiveStore.getState().setOrganizationContext(null, null);
      return null;
    }

    setActiveOrganizationId(orgId);
    writeActiveOrganizationId(orgId);

    useMeetingLiveStore.getState().setOrganizationContext(orgId, active.organization_name || null);
    writeCachedOrgContext({
      organizationId: orgId,
      organizationName: active.organization_name || '',
      userId: active.user_id,
    });

    await safeLocal(() =>
      upsertOrganizationLocal({
        id: orgId,
        name: active.organization_name || 'Organization',
        quorum_threshold: active.quorum_threshold ?? 50,
        bylaws_config: active.bylaws_config ?? {},
      })
    );
    // Pulls are best-effort and must not block navigation
    void safeLocal(() => pullCommitteesIntoRxDB(orgId));
    return active;
  }, []);

  const refreshMemberships = useCallback(async () => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setProfile(null);
      setMemberships([]);
      setActiveOrganizationId(null);
      return [];
    }

    let user = null;
    try {
      const userRes = await supabase.auth.getUser();
      user = userRes?.data.user ?? null;
    } catch (err) {
      console.warn('[Auth] getUser failed', err?.message || err);
      return [];
    }

    if (!user) {
      setProfile(null);
      setMemberships([]);
      setActiveOrganizationId(null);
      setIsPlatformAdmin(false);
      return [];
    }

    try {
      const { data: profileRow } = await supabase
        .from('profiles')
        .select('id, display_name, first_name, last_name')
        .eq('id', user.id)
        .maybeSingle();

      if (profileRow) {
        const meta = user.user_metadata || {};
        const first = profileRow.first_name || meta.first_name || '';
        const last = profileRow.last_name || meta.last_name || '';
        const display =
          [first, last].filter(Boolean).join(' ') ||
          profileRow.display_name ||
          user.email ||
          '';
        setProfile({
          id: profileRow.id,
          display_name: display,
          first_name: first,
          last_name: last,
        });
        await safeLocal(() =>
          upsertProfileLocal({
            id: profileRow.id,
            display_name: display,
          })
        );
      } else {
        const meta = user.user_metadata || {};
        const first = String(meta.first_name || '').trim();
        const last = String(meta.last_name || '').trim();
        const display =
          [first, last].filter(Boolean).join(' ') ||
          String(meta.display_name || '').trim() ||
          user.email ||
          'Officer';
        await supabase.from('profiles').upsert({
          id: user.id,
          first_name: first || null,
          last_name: last || null,
          display_name: display,
        });
        setProfile({ id: user.id, display_name: display, first_name: first, last_name: last });
        await safeLocal(() => upsertProfileLocal({ id: user.id, display_name: display }));
      }

      const { data, error } = await supabase
        .from('organization_memberships')
        .select(
          'id, user_id, organization_id, role, modules, is_active, organizations ( name, quorum_threshold, bylaws_config )'
        )
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (error) {
        console.warn('[Auth] memberships load failed', error.message);
        setMemberships([]);
        return [];
      }

      const mapped = (data ?? []).map(mapMembershipRow);
      setMemberships(mapped);
      membershipsRef.current = mapped;

      // UI GATE ONLY. Real authorization for the platform console lives
      // server-side in lib/platformAuth.js, which re-checks platform_admins
      // with the service role on every request. This just decides whether to
      // render the nav entry and the /platform route.
      //
      // Isolated try/catch, and `.rpc()` returns { error } rather than throwing:
      // SQL here is applied by hand (supabase/DEPLOY.md) so code always ships
      // before the migration lands. A missing function must degrade to `false`,
      // never break the membership load.
      try {
        const { data: isPa, error: paErr } = await supabase.rpc('is_platform_admin');
        setIsPlatformAdmin(!paErr && isPa === true);
      } catch {
        setIsPlatformAdmin(false);
      }

      await Promise.all(
        mapped.map((m) =>
          safeLocal(() =>
            upsertMembershipLocal({
              id: m.id,
              user_id: m.user_id,
              organization_id: m.organization_id,
              role: m.role,
              modules: m.modules,
              is_active: m.is_active,
              organization_name: m.organization_name,
            })
          )
        )
      );

      const preferred = readActiveOrganizationId();
      const preferredOk = preferred && mapped.some((m) => m.organization_id === preferred);
      let nextOrgId = null;
      if (preferredOk) nextOrgId = preferred;
      else if (mapped.length === 1) nextOrgId = mapped[0].organization_id;

      if (nextOrgId) {
        await applyActiveOrg(nextOrgId, mapped);
      } else if (mapped.length === 0) {
        setActiveOrganizationId(null);
        writeActiveOrganizationId(null);
      }

      return mapped;
    } catch (err) {
      console.warn('[Auth] refreshMemberships failed', err?.message || err);
      return [];
    }
  }, [applyActiveOrg]);

  const refreshMembershipsRef = useRef(refreshMemberships);
  useEffect(() => {
    refreshMembershipsRef.current = refreshMemberships;
  }, [refreshMemberships]);

  const switchOrganization = useCallback(
    async (orgId) => {
      let list = membershipsRef.current;
      if (!list.length) {
        const supabase = getSupabaseClient();
        if (!supabase) return;
        try {
          const { data } = await supabase
            .from('organization_memberships')
            .select(
              'id, user_id, organization_id, role, modules, is_active, organizations ( name, quorum_threshold, bylaws_config )'
            )
            .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
            .eq('is_active', true);
          list = (data ?? []).map(mapMembershipRow);
          setMemberships(list);
          membershipsRef.current = list;
        } catch (err) {
          console.warn('[Auth] switchOrganization load failed', err?.message || err);
          return;
        }
      }
      await applyActiveOrg(orgId, list);
    },
    [applyActiveOrg]
  );

  const hasModule = useCallback(
    (moduleName) => {
      const active = memberships.find(
        (m) => m.organization_id === activeOrganizationId && m.is_active
      );
      return membershipHasModule(active, moduleName);
    },
    [memberships, activeOrganizationId]
  );

  useEffect(() => {
    const supabase = getSupabaseClient();

    if (!hasSupabaseConfig() || !supabase) {
      setSession(null);
      setProfile(null);
      setMemberships([]);
      setAllowOfflineAccess(false);
      setReady(true);
      return;
    }

    let cancelled = false;

    // Mark UI ready quickly; hydrate memberships without blocking forever.
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;
        setSession(data.session ?? null);
        if (data.session?.user) {
          // Defer so we are not inside any auth lock
          setTimeout(() => {
            void refreshMembershipsRef.current();
          }, 0);
        } else {
          const cached = readCachedOrgContext();
          if (cached && typeof navigator !== 'undefined' && !navigator.onLine) {
            useMeetingLiveStore.getState().setOrganizationContext(
              cached.organizationId,
              cached.organizationName
            );
            setActiveOrganizationId(cached.organizationId);
            setAllowOfflineAccess(true);
          }
        }
      } catch (err) {
        console.warn('[Auth] getSession failed', err?.message || err);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    // IMPORTANT: do not await Supabase calls directly inside this callback —
    // it holds an auth lock and can deadlock (infinite "Loading…").
    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      if (newSession?.user) {
        setAllowOfflineAccess(false);
        setTimeout(() => {
          void refreshMembershipsRef.current();
        }, 0);
      } else {
        setProfile(null);
        setMemberships([]);
        membershipsRef.current = [];
        setActiveOrganizationId(null);
        setIsPlatformAdmin(false);
        useMeetingLiveStore.getState().reset();
        writeCachedOrgContext(null);
        writeActiveOrganizationId(null);
        setAllowOfflineAccess(false);
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const goOffline = () => {
      if (session) return;
      const cached = readCachedOrgContext();
      if (cached) {
        useMeetingLiveStore.getState().setOrganizationContext(
          cached.organizationId,
          cached.organizationName
        );
        setActiveOrganizationId(cached.organizationId);
        setAllowOfflineAccess(true);
      }
    };
    window.addEventListener('offline', goOffline);
    return () => window.removeEventListener('offline', goOffline);
  }, [session]);

  const signOut = useCallback(async () => {
    const supabase = getSupabaseClient();
    useMeetingLiveStore.getState().reset();
    writeCachedOrgContext(null);
    writeActiveOrganizationId(null);
    setAllowOfflineAccess(false);
    setProfile(null);
    setMemberships([]);
    membershipsRef.current = [];
    setActiveOrganizationId(null);
    setIsPlatformAdmin(false);
    if (supabase) await supabase.auth.signOut();
    setSession(null);
  }, []);

  const activeMembership = useMemo(
    () =>
      memberships.find((m) => m.organization_id === activeOrganizationId && m.is_active) ?? null,
    [memberships, activeOrganizationId]
  );

  const value = useMemo(
    () => ({
      ready,
      session,
      user: session?.user ?? null,
      profile,
      memberships,
      activeOrganizationId,
      activeMembership,
      isPlatformAdmin,
      allowOfflineAccess: allowOfflineAccess && !session,
      refreshMemberships,
      refreshProfile: refreshMemberships,
      switchOrganization,
      hasModule,
      signOut,
    }),
    [
      ready,
      session,
      profile,
      memberships,
      activeOrganizationId,
      activeMembership,
      isPlatformAdmin,
      allowOfflineAccess,
      refreshMemberships,
      switchOrganization,
      hasModule,
      signOut,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
